// @flow
import * as React from 'react';
import type { ChildrenArray, Element } from 'react';
import * as ReactIs from 'react-is';

import type { Location } from '../validation/runtime-checks/index.js';
import { Question } from '../Questions.js';
import { Parts, Part } from '../Parts.js';
import color from '../color.js';

export function InternalPathways(props: {|
  elementTree: ChildrenArray<Element<typeof Question>>,
  performValidation: boolean,
  questionsGroupChildrenLocation?: Location,
  addErrors: (errors: Array<{ message: string, ...Location }>) => void,
|}) {
  const questionsByPathway = extractQuestionsByPathway(props.elementTree);

  // Ad-hoc validation to prevent "occlusions"
  React.useEffect(() => {
    if (!props.performValidation) return;
    const occlusionErrors = detectOcclusions(questionsByPathway);

    if (occlusionErrors.length > 0) {
      props.addErrors(
        occlusionErrors.map((message) => ({
          message,
          // We highlight the entire question group as the error, as there isn't
          // a great way to the specific part that is causing the occlusion, since
          // its the interaction of various pieces of the code (errors ranges can't
          // be non-contiguous)
          ...(props.questionsGroupChildrenLocation ?? {}: any),
        })),
      );
    }
  });

  // Worksheets in certain markets will not have any pathways. As such
  // if nothing has been tagged with a pathway, we don't render the component
  // at all.
  if (Object.values(questionsByPathway).flat().length === 0) return null;

  return (
    <>
      <div
        style={{
          // TODO flexbox will probs break the browser printing subsystem
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: color.porcelain,
          borderRadius: 8,
          overflow: 'clip',
        }}
      >
        {Object.keys(questionsByPathway).map((pathway, _, keys) => {
          const questionPaths = questionsByPathway[pathway];
          const questionList = getConciseQuestionList(questionPaths);
          return (
            <div
              key={pathway}
              style={{
                padding: 16,
                width: `${100 / keys.length}%`,
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {userFacingPathwayTitle[pathway]}
              </div>
              <div>{questionList}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Internally we use more logical naming for the pathways, but
// we expose "cute" names to the end users which are liable to
// change. We don't want our documents coupled to these random
// names, so we'll map to them here.
const userFacingPathwayTitle = {
  easy: 'Explorer',
  medium: 'Adventurer',
  hard: 'Trailblazer',
};

// A map from 'pathway', to a list of "question" node paths.
// These node paths look like ['3', 0, 3], which would mean
// question '3', part a), subpart iv).
type QuestionsByPathway = {|
  easy: Array<QuestionPath>,
  medium: Array<QuestionPath>,
  hard: Array<QuestionPath>,
|};

// The more specific/descriptive type is:
// [questionLabel?: string, partIndex?: number | string, subPartIndex?: number | string]
// Note that use of string on partIndex/subPartIndex implies some kind of abuse
type QuestionPath = Array<string | number>;

function extractQuestionsByPathway(
  root: ChildrenArray<Element<typeof Question>>,
): QuestionsByPathway {
  // 🚨 This gets mutated in traverseReactNodeTree
  const questionsByPathway: QuestionsByPathway = {
    easy: [],
    medium: [],
    hard: [],
  };

  React.Children.forEach(root, (node) => {
    traverseReactNodeTree(node, {
      questionPath: [],
      parent: null,
      questionsByPathway,
    });
  });

  return questionsByPathway;
}

function traverseReactNodeTree(
  node: React$Node,
  context: {
    // The question/part ancestor element path of the current node.
    questionPath: QuestionPath,
    // We need to access the parent when determining the index of
    // a Part element, within it's parent Parts element.
    parent: React$Element<any> | null,
    // 🚨 This gets mutated during the recursion
    questionsByPathway: QuestionsByPathway,
  },
) {
  if (ReactIs.isElement(node) === false) return;

  // Manual coercion for Flow since it doesn't know what type ReactIs.isElement enforces.
  const element: React$Element<
    (props: { [key: string]: any, children?: React$Node }) => React$Node,
  > = (node: any);

  const component = getComponent(element);

  let questionPath: QuestionPath = context.questionPath;

  if (component === Question || component === Part) {
    if (component === Question) {
      questionPath = [...questionPath, element.props.label];
    } else if (component === Part) {
      if (context.parent === null) throw Error('Part should have a parent set');
      const partLabelOrIndex =
        element.props.label ??
        childrenAsArray(context.parent.props.children).indexOf(element);
      questionPath = [...questionPath, partLabelOrIndex];
    }

    if (element.props.pathways) {
      for (const pathway of element.props.pathways) {
        // We have to be defensive here as this code will execute even if a descendent
        // Question/Part fails validation (eg by specifying an illegal pathway). We'll
        // simply skip these, as the validation should pick up the mistake.
        if (pathway in context.questionsByPathway) {
          context.questionsByPathway[pathway].push(questionPath);
        }
      }
    }
  }

  // Determine if there is a "superparts" hack going on in the current
  // element. When we encounter a "superpart" we will prevent traversal
  // into it, as it is not allowed to contribute to the worksheet's pathways.
  // Possibly in the future we will turn this hack into a <SuperParts>
  // component or something and then we can unwind this code.
  let superPartsElementIdx = null;
  if (component === Question) {
    const children = React.Children.toArray(element.props.children);
    const partsComponents = children.filter(
      (child) => ReactIs.isElement(child) && getComponent(child) === Parts,
    );
    // TODO need to enforce validation that there are at most 2
    // partsComponents per Question in all our documents
    if (partsComponents.length > 1) {
      const maybeSuperPart = partsComponents[0];
      let isSuperPart = true;
      React.Children.forEach(maybeSuperPart.props.children, (child) => {
        const { label } = child.props;
        if (label === undefined || !romanNumerals.includes(label)) {
          isSuperPart = false;
        }
      });
      if (isSuperPart) {
        // NOTE we use idx, not an element reference, as referential equality
        // gets broken by these React.Children APIs.
        superPartsElementIdx = children.indexOf(partsComponents[0]);
      }
    }
  }

  React.Children.forEach(element.props.children, (child, idx) => {
    // Do NOT traverse into "superparts" component, they are not allowed
    // to contribute to the documents pathways.
    if (superPartsElementIdx === idx) return;
    traverseReactNodeTree(child, {
      questionPath,
      parent: element,
      questionsByPathway: context.questionsByPathway,
    });
  });
}

// React.Children.toArray is annoying as it changes the referential
// identity of each child. So we'll just write our own simple helper
// to normalize T | T[] to T[] so we can operate on children consistently.
function childrenAsArray(children: React$Node) {
  if (Array.isArray(children)) return children;
  return [children];
}

// Precondition: 0 <= idx <= 26
function indexToAlpha(idx: number) {
  return String.fromCharCode(97 + idx);
}

// Precondition: 0 <= idx <= 10
function indexToRoman(idx: number) {
  return romanNumerals[idx];
}
// prettier-ignore
const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];

// We have this annoying pattern for the CartesianPlane components
// where we wrap the actual component in a HoC. Once we roll out
// the new validation scheme we'll be able to unwind those HoCs
// and then unwind this function.
function getComponent(element: React$Element<any>) {
  return element.type.__wrappedComponent ?? element.type;
}

// Given a list of node paths for questions to be included in a given
// pathway, produces a tree structure for the purposes of detecting
// "occlusions". Where occlusions are defined as some ancestor being
// tagged with a pathway, when there are descendents that are also
// being tagged with the same pathway.
function pathwayTreeFromNodePaths(
  nodePaths: Array<QuestionPath>,
): PathwayTreeNode {
  const root: PathwayTreeNode = {
    children: null,
    isTagged: false,
    questionPath: [],
  };

  for (const path of nodePaths) {
    let node = root;
    for (let i = 0; i < path.length; i++) {
      const questionOrPart = path[i];
      if (node.children === null) {
        node.children = {};
      }
      if (node.children[questionOrPart] === undefined) {
        // $FlowIssue not refining the null away
        node.children[questionOrPart] = {
          children: null,
          isTagged: i === path.length - 1,
          questionPath: path.slice(0, path.indexOf(questionOrPart) + 1),
        };
      }
      node = node.children[questionOrPart];
    }
  }

  return root;
}

function detectOcclusions(questionsByPathway: QuestionsByPathway) {
  // Appended to inside detectOcclusionsForPathway
  const errorMessages = [];

  for (const pathway of Object.keys(questionsByPathway)) {
    const tree = pathwayTreeFromNodePaths(questionsByPathway[pathway]);
    detectOcclusionsForPathway(pathway, tree, errorMessages);
  }

  return errorMessages;
}

function detectOcclusionsForPathway(
  pathway: $Keys<QuestionsByPathway>,
  node: PathwayTreeNode,
  // 🚨 mutated during recursion
  errorMessages: Array<string>,
) {
  // If a non-leaf node is tagged, then there exists some descendent which is
  // also tagged. Simple example: 3a is tagged, and 3a(i) is tagged. 3a being
  // tagged with a pathway "occludes" 3a(i) being tagged with that same pathway
  if (node.isTagged && node.children !== null) {
    const pathStr = node.questionPath
      .map(normalizePathSegmentLabel)
      .map((label, idx) => (idx === 2 ? `(${label})` : label))
      .join('');
    errorMessages.push(
      `Question ${pathStr} specifies the "${pathway}" pathway, but also contains descendent Part components with the "${pathway}" pathway specified. This is not allowed. Either remove the "${pathway}" pathway from ${pathStr}, or remove the "${pathway}" pathway from all descendent Part component(s).`,
    );
  }

  if (node.children === null) return;

  for (const key of Object.keys(node.children)) {
    const child = node.children[key];
    detectOcclusionsForPathway(pathway, child, errorMessages);
  }
}

type PathwayTreeNode = {|
  children: { [string | number]: PathwayTreeNode } | null,
  // isTagged means that this node been "tagged" with the pathway
  // in the JSX. Eg pathways={['easy']}.
  isTagged: boolean,
  questionPath: QuestionPath,
|};

// ----------------------------------------------------------------------------
//  #region Logic for computing the concise labelling
// ----------------------------------------------------------------------------

/**
 * Concise labelling has the following behaviour:
 *
 * - Consecutive numbers are collapsed into a range.
 *   Example: 1, 2, 3, 4 -> 1–4
 *
 * - Sibling letters (need not be consecutive) get concatenated.
 *   Example: 1a, 1b, 1d -> 1abd
 *
 * - Sibling roman numerals (need not be consecutive) get concatenated.
 *   Example: 1ai, 1aii, 1av -> 1a(i)(ii)(v)
 *
 * Note that neither roman numerals nor letters are collapsed into ranges.
 * We can concatenate them unambiguously (unlike numbers) which is fairly
 * space efficient. Trying to do range collapse leads to less concision:
 * - Concatenation: 1abceghik
 * - Range collapse: 1a–c, 1e, 1g–i, 1k
 */
function getConciseQuestionList(questionPaths: Array<QuestionPath>) {
  const questionPathsWithNormalizedLabels = normalizePathLabels(questionPaths);
  const tree = makeTree(questionPathsWithNormalizedLabels);
  const leaves = getLeaves(tree);
  const ranges = getRanges(leaves);
  const labels = getLabels(ranges);
  return labels.join(', ');
}

// parts and subparts can be strings or numbers, we normalize
// everything to strings here
function normalizePathLabels(questionPaths: Array<QuestionPath>) {
  return questionPaths.map((path) => path.map(normalizePathSegmentLabel));
}

function normalizePathSegmentLabel(labelOrIndex: string | number, idx: number) {
  if (idx === 0) {
    if (typeof labelOrIndex !== 'string') throw Error('invariant violated');
    return labelOrIndex;
  }
  if (idx === 1) {
    if (typeof labelOrIndex === 'number') {
      return indexToAlpha(labelOrIndex);
    } else {
      return labelOrIndex;
    }
  }
  if (idx === 2) {
    if (typeof labelOrIndex === 'number') {
      return indexToRoman(labelOrIndex);
    } else {
      return labelOrIndex;
    }
  }
  throw Error('invariant violated');
}

type TreeNode = {
  type: 'number' | 'letter' | 'roman',
  label: string,
  children: Array<TreeNode> | null,
  parent: TreeNode | null,
};

// Precondition: questionPaths is in question sorted order
function makeTree(questionPaths: Array<Array<string>>) {
  const root: TreeNode = {
    type: 'number', // fake - won't be used
    label: 'root', // fake - won't be used
    children: null,
    parent: null,
  };
  for (const path of questionPaths) {
    let curNode = root;
    for (let i = 0; i < path.length; i++) {
      const questionOrPart = path[i];
      const node = curNode.children?.find(
        (child) => child.label === questionOrPart,
      );
      if (node) {
        curNode = node;
      } else {
        const newChild: TreeNode = {
          type: depthToType(i),
          label: questionOrPart,
          children: null,
          parent: curNode,
        };
        if (curNode.children === null) curNode.children = [];
        curNode.children.push(newChild);
        curNode = newChild;
      }
    }
  }
  return root;
}

function depthToType(depth: number) {
  if (depth === 0) return 'number';
  if (depth === 1) return 'letter';
  if (depth === 2) return 'roman';
  throw Error('Invalid depth');
}

function getLeaves(node: TreeNode): Array<TreeNode> {
  if (node.children === null) return [node];
  return node.children.flatMap(getLeaves);
}

function ancestors(node: TreeNode): Array<TreeNode> {
  if (node.parent === null) return [];
  return [...ancestors(node.parent), node];
}

type NodeRange = {
  type: 'number' | 'letter' | 'roman',
  nodes: Array<TreeNode>,
};
function getRanges(leaves: Array<TreeNode>) {
  if (leaves.length === 0) return [];

  const ranges: Array<NodeRange> = [];
  let curRange: NodeRange = {
    type: leaves[0].type,
    nodes: [leaves[0]],
  };

  for (let i = 0; i < leaves.length - 1; i++) {
    const cur = leaves[i];
    const next = leaves[i + 1];

    if (curRange.type === 'number') {
      if (isSubsequentNumber(cur, next)) {
        curRange.nodes.push(next);
      } else {
        ranges.push(curRange);
        curRange = {
          type: next.type,
          nodes: [next],
        };
      }
    } else {
      if (isSibling(cur, next)) {
        curRange.nodes.push(next);
      } else {
        ranges.push(curRange);
        curRange = {
          type: next.type,
          nodes: [next],
        };
      }
    }
  }
  if (curRange === null) throw Error('invariant violated');
  ranges.push(curRange);

  return ranges;
}

function getLabels(ranges: Array<NodeRange>) {
  const labels: Array<string> = [];
  for (const { type, nodes } of ranges) {
    const start = nodes[0];
    const end = nodes[nodes.length - 1];
    if (start === end) {
      labels.push(pathToLabel(ancestors(start)));
    } else {
      if (type === 'number') {
        labels.push(`${pathToLabel(ancestors(start))}-${end.label}`);
      } else if (type === 'letter') {
        const subsequentLetters = nodes
          .slice(1)
          .map((n) => n.label)
          .join('');
        labels.push(pathToLabel(ancestors(start)) + subsequentLetters);
      } else if (type === 'roman') {
        const subsequentRomans = nodes
          .slice(1)
          .map((n) => `(${n.label})`)
          .join('');
        labels.push(pathToLabel(ancestors(start)) + subsequentRomans);
      }
    }
  }
  return labels;
}

function isSubsequentNumber(a: TreeNode, b: TreeNode) {
  if (a.type !== 'number' || b.type !== 'number') return false;
  if (!isSibling(a, b)) return false;

  const aVal = parseInt(a.label);
  const bVal = parseInt(b.label);
  return aVal + 1 === bVal;
}

function isSibling(a: TreeNode, b: TreeNode) {
  const aAncestors = ancestors(a);
  const bAncestors = ancestors(b);
  if (aAncestors.length !== bAncestors.length) return false;
  // We don't consider the final part of the path, since ancestors is self-inclusive
  for (let i = 0; i < aAncestors.length - 1; i++) {
    if (aAncestors[i] !== bAncestors[i]) return false;
  }
  return true;
}

function pathToLabel(path: Array<TreeNode>) {
  return path
    .map((n) => {
      if (n.type === 'roman') return `(${n.label})`;
      return n.label;
    })
    .join('');
}

// ----------------------------------------------------------------------------
//  #endregion Logic for computing the concise labelling
// ----------------------------------------------------------------------------
